import { Theme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';

import { keyframes } from '@emotion/css';


const fadeInDigit = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const useStyles = makeStyles(({
  breakpoints,
  fontFamilies,
  palette,
  lineHeights,
  fontSizes,
  fontWeights,
}: Theme) => ({
  hint: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.xs,
    lineHeight: lineHeights.sm,
    color: palette.grey['700'],
    margin: '0 0 0.625rem',
    [breakpoints.up('md')]: {
      marginBottom: '1rem',
      fontSize: fontSizes.sm,
      lineHeight: lineHeights.md,
    },
  },
  barcode: {
    width: 'auto',
    maxWidth: '100%',
    height: '3.5rem',
    marginBottom: 0,
    [breakpoints.up('md')]: {
      height: '5.3125rem',
    },
  },
  id: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 'fit-content',
    margin: '0 auto 0.375rem',
    padding: '0.375rem 0.875rem 0.375rem 1.5rem',
    borderRadius: '0.5rem',
    backgroundColor: palette.grey[200],
    color: palette.common.black,
    fontWeight: fontWeights.medium,
    letterSpacing: '0.625rem',
    fontSize: '1.25rem',
    lineHeight: 1.4,
    [breakpoints.up('md')]: {
      marginBottom: '0.5rem',
      padding: '0.625rem 2.5rem 0.625rem 2.75rem',
      letterSpacing: '0.25rem',
    },
  },
  propsContentStyles: {
    padding: '0 1.5rem',
    [breakpoints.up('sm')]: {
      padding: '0 3rem',
    },
  },
  otpWrapper: {
    display: 'flex',
    justifyContent: 'center',
    gap: '0.5rem',
  },
  otpDigit: {
    width: '2.5rem',
    height: '2.5rem',
    border: 'none',
    borderRadius: '0.5rem',
    backgroundColor: palette.primary.dark,
    color: palette.common.white,
    fontSize: '1.25rem',
    fontWeight: fontWeights.medium,
    lineHeight: 1.4,
    animation: `${fadeInDigit} 2s ease`,
    [breakpoints.up('sm')]: {
      width: '3rem',
      height: '3rem',
      fontSize: '1.5rem',
    },
  },
  digitText: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    margin: '0 auto',
    opacity: 0,
    animation: `${fadeInDigit} 1s ease forwards`,
    color: palette.common.white,
  },
  resendWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '0.5rem',
    margin: '0.625rem auto 0',
    [breakpoints.up('md')]: {
      marginTop: '1rem',
    },
  },
  cooldownText: {
    margin: 0,
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: palette.grey[700],
    fontWeight: fontWeights.regular,
    [breakpoints.up('md')]: {
      fontSize: '1rem',
    },
  },
  resendLink: {
    background: 'none',
    border: 'none',
    fontSize: '0.875rem',
    color: palette.primary.dark,
    fontWeight: fontWeights.medium,
    cursor: 'pointer',
    padding: 0,
    letterSpacing: '1px',
    [breakpoints.up('md')]: {
      fontSize: '1rem',
    },
  },
  divider: {
    height: 1,
    width: 'calc(100% + 3rem)',
    margin: '0.625rem -1.5rem',
    backgroundColor: palette.grey[400],
    [breakpoints.up('sm')]: {
      width: 'calc(100% + 4rem)',
      margin: '1.5rem -2rem',
    },
    [breakpoints.up('md')]: {
      width: 'calc(100% + 4rem)',
      margin: '1.5rem -2rem',
    },
  },
  otpTitle: {
    margin: 0,
    fontSize: '1rem',
    lineHeight: 1.5,
    fontWeight: fontWeights.medium,
    [breakpoints.up('md')]: {
      fontSize: '1.75rem',
    },
  },
  otpSubtitle: {
    marginTop: 0,
    marginBottom: '0.5rem',
    color: palette.grey[700],
    fontSize: '0.875rem',
    lineHeight: 1.5,
    [breakpoints.up('md')]: {
      marginBottom: '1rem',
      fontSize: '1rem',
    },
  },
  paper: {
    paddingTop: '1.5rem',
    paddingBottom: '1.5rem',
    borderRadius: '0.5rem',
    lineHeight: '0.001em',
    textAlign: 'center',
    [breakpoints.up('md')]: {
      paddingTop: '2.5rem',
      paddingBottom: '2.5rem',
    },
  },
  text: {
    marginLeft: '5px',
    fontSize: '0.875rem',
    lineHeight: 1.5,
    [breakpoints.up('md')]: {
      fontSize: '1rem',
    },
  },
  walletButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.75rem',
    backgroundColor: palette.grey[50],
    border: `1px solid ${palette.grey[200]}`,
    borderRadius: '1rem',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    textAlign: 'left',
    height: '4.25rem',
    margin: '0 auto',
    textDecoration: 'none',
    color: 'inherit',
    '&:hover': {
      backgroundColor: palette.grey[100],
    },
  },
  walletIcon: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '2.75rem',
    height: '2.75rem',
    '& svg': {
      width: '2.75rem',
      height: '2.75rem',
    },
  },
  walletContent: {
    flex: 1,
    textAlign: 'left',
    overflow: 'hidden',
  },
  walletTitle: {
    fontFamily: fontFamilies.primary,
    fontSize: '0.875rem',
    lineHeight: 1.5,
    fontWeight: fontWeights.medium,
    color: palette.common.black,
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [breakpoints.up('md')]: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
  },
  walletDescription: {
    margin: '0.5rem 0 0',
    fontSize: '0.625rem',
    lineHeight: 1.4,
    color: palette.grey[600],
    textAlign: 'left',
    fontFamily: fontFamilies.primary,
    [breakpoints.up('md')]: {
      fontSize: '0.75rem',
      lineHeight: 1.5,
    },
  },
  chevronIcon: {
    flexShrink: 0,
    width: '1.5rem',
    height: '1.5rem',
  },
}));